// Pulls analytics trades for a game by paginating /api/analytics/trades.
//
// Two modes:
//   - 'event' (default): only load trades from the event-start window
//     onward. Pages come back ts DESC, so we keep batched-paginating
//     until a returned page contains trades older than eventStartMs;
//     that page gets filtered to ts >= eventStartMs and pagination
//     stops. Trims the deep pre-game tail (often days/weeks of low-
//     volume early trades) and is the right shape for the chart +
//     trades-table default view.
//   - 'all': original behavior — pull every trade hydra has for the
//     game. Used when the user explicitly clicks ALL.
//
// Why a custom hook (not useAnalyticsResource):
//   Hydra hard-caps the /trades endpoint at 500 rows per request and
//   exposes no time-range filter (see [[project_game_detail_hydra_cap_500]]
//   for the cap and the broader hydra ask). High-volume games hit
//   40k+ trades — ~80 round-trips at 500 each — so we batch in
//   parallel, surface partial results to the UI as they land, and
//   (in event mode) stop early once we've crossed the event-start
//   threshold.
//
// Live polling:
//   When `pollEnabled` is true, after the initial load completes the
//   hook polls offset=0 every POLL_INTERVAL_MS and prepends any rows
//   whose ts is newer than the newest-known ts. Caller should set this
//   true only while the game is still trading (gameState !== 'final')
//   and unset it once the game ends — see GameDetail/index.tsx.

import { useEffect, useRef, useState } from 'react';
import {
  AnalyticsError,
  getTrades,
  hasAnalytics,
  type TradeRow,
} from '@/services/hydraAnalytics';
import { earliestEventStart } from './helpers';

const PAGE_SIZE = 500;        // matches hydra's hard cap
const CONCURRENCY = 6;        // parallel requests per batch — empirical sweet
                              //   spot vs hammering hydra
const POLL_INTERVAL_MS = 5000;

export type GameTradesStatus = 'idle' | 'loading' | 'ok' | 'error';
export type GameTradesMode = 'event' | 'all';

export interface GameTradesResult {
  status: GameTradesStatus;
  items: TradeRow[];
  loaded: number;
  total: number;
  error: { message: string; status: number; code?: string } | null;
}

export function useGameTrades(
  canonicalKey: string | null,
  marketType: string,
  mode: GameTradesMode = 'event',
  pollEnabled = false,
): GameTradesResult {
  const [state, setState] = useState<GameTradesResult>({
    status: 'idle',
    items: [],
    loaded: 0,
    total: 0,
    error: null,
  });

  // Newest known ts (decode-time, ms). Tracked via ref so the polling
  // effect doesn't recreate on every items update. Updated by both the
  // initial load (below) and successful polls.
  const newestTsMsRef = useRef(0);

  useEffect(() => {
    if (!canonicalKey || !hasAnalytics()) {
      setState({ status: 'idle', items: [], loaded: 0, total: 0, error: null });
      newestTsMsRef.current = 0;
      return;
    }

    const ctrl = new AbortController();
    let cancelled = false;

    const run = async () => {
      setState({
        status: 'loading',
        items: [],
        loaded: 0,
        total: 0,
        error: null,
      });
      newestTsMsRef.current = 0;

      try {
        const first = await getTrades(
          { gameKey: canonicalKey, marketType, limit: PAGE_SIZE, offset: 0 },
          ctrl.signal,
        );
        if (cancelled) return;

        const collected: TradeRow[] = [...first.items];
        const total = first.total;
        bumpNewestTs(newestTsMsRef, collected);
        setState({
          status: 'loading',
          items: collected.slice(),
          loaded: collected.length,
          total,
          error: null,
        });

        if (collected.length >= total) {
          setState({
            status: 'ok',
            items: collected,
            loaded: collected.length,
            total,
            error: null,
          });
          return;
        }

        // Event-mode threshold: ms timestamp below which we stop paging.
        // Derived from Kalshi tickers in the trades we've collected so
        // far. Null when no Kalshi ticker yields a start time — in that
        // case event mode degrades silently to all-mode behavior (we
        // can't draw a meaningful cutoff line).
        let eventStartMs: number | null =
          mode === 'event' ? earliestEventStart(collected) : null;

        // Build the list of remaining offsets at PAGE_SIZE intervals.
        const offsets: number[] = [];
        for (let o = collected.length; o < total; o += PAGE_SIZE) {
          offsets.push(o);
        }

        // Walk offsets in parallel batches. After each batch in event
        // mode, refresh eventStartMs from the now-larger collected set
        // (covers the case where page 0 was Poly-only) and check if
        // any trade in the batch crossed below the threshold — if so,
        // filter the tail and stop.
        for (let i = 0; i < offsets.length; i += CONCURRENCY) {
          if (cancelled) return;
          const batch = offsets.slice(i, i + CONCURRENCY);
          const results = await Promise.all(
            batch.map((offset) =>
              getTrades(
                { gameKey: canonicalKey, marketType, limit: PAGE_SIZE, offset },
                ctrl.signal,
              ),
            ),
          );
          if (cancelled) return;

          const batchTrades: TradeRow[] = [];
          for (const r of results) batchTrades.push(...r.items);

          if (mode === 'event') {
            // Re-derive in case page 0 had no Kalshi.
            if (eventStartMs == null) {
              eventStartMs = earliestEventStart([...collected, ...batchTrades]);
            }
            if (eventStartMs != null) {
              const cutoff = eventStartMs;
              // Trades in this batch that are still inside the window.
              const inWindow = batchTrades.filter((t) => {
                const ts = Date.parse(t.block_ts ?? t.ts);
                return Number.isFinite(ts) && ts >= cutoff;
              });
              const crossed = inWindow.length < batchTrades.length;
              collected.push(...inWindow);
              bumpNewestTs(newestTsMsRef, inWindow);
              setState({
                status: crossed ? 'ok' : 'loading',
                items: collected.slice(),
                loaded: collected.length,
                total,
                error: null,
              });
              if (crossed) return;
              continue;
            }
          }

          collected.push(...batchTrades);
          bumpNewestTs(newestTsMsRef, batchTrades);
          setState({
            status: 'loading',
            items: collected.slice(),
            loaded: collected.length,
            total,
            error: null,
          });
        }

        setState({
          status: 'ok',
          items: collected,
          loaded: collected.length,
          total,
          error: null,
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof AnalyticsError) {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: { message: err.message, status: err.status, code: err.code },
          }));
        } else if (err instanceof Error) {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: { message: err.message, status: 0 },
          }));
        } else {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: { message: 'Unknown error', status: 0 },
          }));
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [canonicalKey, marketType, mode]);

  // Incremental polling: once the initial load is OK, fetch offset=0
  // every 5s and prepend any rows newer than newest-known. Hydra orders
  // ts DESC so we can short-circuit the moment we hit a row we've seen.
  // Compares by `ts` (decode-time) — that's the field that increases
  // monotonically with DB insertion order, regardless of block_ts.
  useEffect(() => {
    if (!pollEnabled) return;
    if (!canonicalKey || !hasAnalytics()) return;

    let cancelled = false;
    const tick = async () => {
      const known = newestTsMsRef.current;
      if (!known) return; // initial load hasn't populated yet
      const ctrl = new AbortController();
      try {
        const resp = await getTrades(
          { gameKey: canonicalKey, marketType, limit: PAGE_SIZE, offset: 0 },
          ctrl.signal,
        );
        if (cancelled) return;
        const newRows: TradeRow[] = [];
        for (const row of resp.items) {
          const ts = Date.parse(row.ts);
          if (!Number.isFinite(ts)) continue;
          if (ts > known) {
            newRows.push(row);
          } else {
            break; // hydra returns ts DESC; remaining are older
          }
        }
        if (newRows.length === 0) return;
        bumpNewestTs(newestTsMsRef, newRows);
        setState((prev) => ({
          ...prev,
          items: [...newRows, ...prev.items],
          loaded: prev.loaded + newRows.length,
          total: Math.max(prev.total, prev.loaded + newRows.length),
        }));
      } catch (err) {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Swallow other errors silently — initial load already surfaces
        // status='error'; we don't want a flaky poll to clobber the
        // loaded data with an error state.
      }
    };

    const interval = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [pollEnabled, canonicalKey, marketType]);

  return state;
}

// Update the newest-known-ts ref to the max of the existing value and
// every row in `rows`. Used by both the initial load and the polling
// tick to keep the boundary current.
function bumpNewestTs(ref: { current: number }, rows: TradeRow[]) {
  for (const r of rows) {
    const t = Date.parse(r.ts);
    if (Number.isFinite(t) && t > ref.current) ref.current = t;
  }
}
